import { Link } from 'react-router-dom';
import type { KpiCard } from '@oxxean/types';
import { formatDelta } from '../lib/format';
import { resolveSemaphore, semaphoreTone, thresholdHint } from '../lib/semaphore';
import { MetricDisplay } from './MetricDisplay';
import { Badge, HelpTip } from './primitives';

/**
 * A qué pantalla de detalle lleva cada KPI. Solo se usa en el dashboard: en la
 * página de un indicador el detalle ya está debajo, así que sus KPI no navegan.
 * Una clave sin ruta simplemente no es pulsable (mejor que un enlace equivocado).
 */
const KPI_ROUTE: Record<string, string> = {
  headcountEnd: '/dotacion',
  headcountStart: '/dotacion',
  headcountAverage: '/dotacion',
  hires: '/ingresos-egresos',
  terminations: '/ingresos-egresos',
  exits: '/ingresos-egresos',
  netChange: '/ingresos-egresos',
  contractsIssued: '/ingresos-egresos',
  settlements: '/ingresos-egresos',
  turnoverPct: '/rotacion',
  turnoverRate: '/rotacion',
  attendanceRate: '/asistencia',
  workedDays: '/asistencia',
  absencePct: '/ausentismo',
  absenceRate: '/ausentismo',
  leaveDays: '/ausentismo',
  latenessCount: '/atrasos',
  overtimeHours: '/horas-extra',
  overtimeCalcHours: '/horas-extra',
  pendingVacationDays: '/vacaciones',
  employeesTwoPeriods: '/vacaciones',
  contractsPct: '/documentacion-laboral',
  annexesPct: '/documentacion-laboral',
  settlementsPct: '/documentacion-laboral',
  riskLevel: '/documentacion-laboral',
};

/**
 * Tarjeta de KPI.
 *
 * No contiene ningún umbral: la clasificación llega en `semaphore` y los cortes
 * vigentes en `semaphore.thresholds` (DEC-A08). El componente sólo los muestra.
 */
export function KpiTile({ kpi, href }: { kpi: KpiCard; href?: string }): JSX.Element {
  const semaphore = resolveSemaphore(kpi.metric.status, kpi.semaphore);
  const hint = thresholdHint(kpi.semaphore?.thresholds, kpi.format);
  const help = [kpi.helpText, hint].filter((part): part is string => part !== undefined && part !== null);

  // La variación sólo tiene sentido si hay una cifra que comparar.
  const showDelta =
    kpi.delta !== null &&
    (kpi.metric.status === 'REAL' ||
      kpi.metric.status === 'PARTIAL' ||
      kpi.metric.status === 'MANUAL');

  const deltaClass =
    kpi.delta === null || kpi.delta === 0
      ? 'kpi__delta--flat'
      : (kpi.delta > 0) === kpi.higherIsWorse
        ? 'kpi__delta--bad'
        : 'kpi__delta--good';

  const inner = (
    <>
      <h4 className="kpi__label">
        {kpi.label}
        {help.length > 0 && <HelpTip text={help.join(' · ')} />}
      </h4>

      {showDelta && kpi.delta !== null && (
        <span
          className={`kpi__delta ${deltaClass}`}
          title={`Variación respecto del período anterior: ${formatDelta(kpi.delta)}`}
        >
          {formatDelta(kpi.delta)}
        </span>
      )}

      <MetricDisplay metric={kpi.metric} format={kpi.format} size="lg" showFraction />

      {semaphore !== null && (
        <div className="kpi__foot">
          <Badge tone={semaphoreTone(semaphore.state)} dot title={hint ?? undefined}>
            {semaphore.label}
          </Badge>
          {semaphore.downgraded && (
            <span className="tiny muted" title="Un semáforo verde sólo puede nacer de datos reales.">
              sin evidencia suficiente
            </span>
          )}
        </div>
      )}
    </>
  );

  // Con destino, la tarjeta ES un enlace al detalle del indicador; el puntero y
  // el estado de foco los aporta la clase `kpi--link`.
  if (href) {
    return (
      <Link className="kpi kpi--link" to={href} data-sem={semaphore?.state ?? 'NONE'} title="Ver detalle">
        {inner}
      </Link>
    );
  }

  return (
    <article className="kpi" data-sem={semaphore?.state ?? 'NONE'}>
      {inner}
    </article>
  );
}

export function KpiGrid({ kpis, linkable = false }: { kpis: KpiCard[]; linkable?: boolean }): JSX.Element {
  return (
    <div className="grid-kpi">
      {kpis.map((kpi) => (
        <KpiTile key={kpi.key} kpi={kpi} href={linkable ? KPI_ROUTE[kpi.key] : undefined} />
      ))}
    </div>
  );
}
